/*  iceberg-ui  */
import { Box, Button, Flex, Icon } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import {
    CircularProgress,
    Divider,
    IconButton,
    TextField,
    Typography
} from '@mui/material';
import { WorkatoConnection } from 'application/Home/Admin/Workato/types';

/*  Shared  */
import { ApplicationNameLinkCell } from 'application/Shared/Components/ApplicationNameLinkCell';
import { Sources } from 'application/Shared/Components/Avatars';
import {
    LifecyclePicker,
    ManagedPicker
} from 'application/Shared/Components/Pickers';
import { SSOSupportIcons } from 'application/Shared/Components/SSOSupportIcons';
import {
    FontSizes,
    LifecycleStatus,
    ManagementState
} from 'application/Shared/Constants';
import { useTenantAppContext } from 'application/Shared/Contexts/TenantAppContext';
import { Helpers, Notify, SSOUtils } from 'application/Shared/Helpers';
import { BulkClassifyModal } from 'application/Shared/Modals/Application/BulkClassifyModal';
import { debug } from 'application/Shared/Utils';
import { TenantApp } from 'application/Shared/types/tenantApp';

/*  Hooks  */
import { useGetDefaultCategoryId } from 'hooks/dataAccess/Category/category';
import { usePutTenantApplicationLink } from 'hooks/dataAccess/Tenant/ApplicationLink/tenantApplicationLink';

/* Objects */
import {
    Application,
    ApplicationCategoryAndSubCategory,
    ApplicationDescription
} from 'objects/Application';

/*  React  */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { SaasOpsState } from './internal/SaasOpsState';

const AppCategoryInfo = () => {
    const { t } = useTranslation();

    return (
        <Box
            data-testid="appHeaderAppCategoryInfo"
            display="flex"
            flexDirection="column"
        >
            <Typography
                variant="body2"
                color="textSecondary"
                sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
            >
                {t('Common.category')}
            </Typography>
            <Box mt="4px">
                <ApplicationCategoryAndSubCategory noTruncate size="normal" />
            </Box>
        </Box>
    );
};

const AppDescriptionInfo = () => {
    const { t } = useTranslation();

    return (
        <Box
            data-testid="appHeaderAppDescriptionInfo"
            display="flex"
            flexDirection="column"
        >
            <Typography
                variant="body2"
                color="textSecondary"
                sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
            >
                {t('Common.description')}
            </Typography>
            <Box mt="4px">
                <ApplicationDescription noTruncate />
            </Box>
        </Box>
    );
};

const SaasOpsInfo = ({
    name,
    refetchTenantApps,
    workatoConnection,
    workatoProvider
}: {
    name: string;
    refetchTenantApps: () => void;
    workatoConnection: WorkatoConnection;
    workatoProvider: string;
}) => {
    const { t } = useTranslation();

    return (
        <Box
            data-testid="appHeaderSaaSOpsInfo"
            display="flex"
            flexDirection="column"
        >
            <Typography
                variant="body2"
                color="textSecondary"
                sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
            >
                {t('SaasOps.integrationStatus')}
            </Typography>
            <Box mt="4px">
                <SaasOpsState
                    name={name}
                    onSuccessFn={() => {
                        refetchTenantApps();
                    }}
                    workatoConnection={workatoConnection}
                    workatoProvider={workatoProvider}
                />
            </Box>
        </Box>
    );
};

const PublisherInfo = ({ publisher }: { publisher: string }) => {
    const { t } = useTranslation();

    return (
        <Box
            data-testid="appHeaderPublisherInfo"
            display="flex"
            flexDirection="column"
        >
            <Typography
                variant="body2"
                color="textSecondary"
                sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
            >
                {t('Common.publisher')}
            </Typography>
            <Box mt="4px">
                <Typography variant="body2">{publisher}</Typography>
            </Box>
        </Box>
    );
};

const LifecycleInfo = ({
    lifecycleStage
}: {
    lifecycleStage: LifecycleStatus;
}) => {
    const { t } = useTranslation();

    return (
        <Box
            data-testid="appHeaderLifecycleInfo"
            display="flex"
            flexDirection="column"
        >
            <Typography
                variant="body2"
                color="textSecondary"
                sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
            >
                {t('Common.lifecycleStage')}
            </Typography>
            <Box mt="4px">
                <LifecyclePicker visualOnly value={lifecycleStage} />
            </Box>
        </Box>
    );
};

const ManagedInfo = ({
    managedVal
}: {
    managedVal: ManagementState | undefined;
}) => {
    const { t } = useTranslation();
    return (
        <Box
            data-testid="appHeaderManagedInfo"
            display="flex"
            flexDirection="column"
        >
            <Typography
                variant="body2"
                color="textSecondary"
                sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
            >
                {t('Common.applicationState')}
            </Typography>
            <Box mt="4px">
                {managedVal ? (
                    <ManagedPicker shortLabel visualOnly value={managedVal} />
                ) : (
                    <Typography>{t('Common.noData')}</Typography>
                )}
            </Box>
        </Box>
    );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const SourceInfo = ({ sources }: { sources: any }) => {
    const { t } = useTranslation();
    return (
        <Box
            data-testid="appHeaderSourceInfo"
            display="flex"
            flexDirection="column"
        >
            <Typography
                variant="body2"
                color="textSecondary"
                sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
            >
                {t('Common.source')}
            </Typography>
            <Box mt="2px">
                <Sources
                    max={3}
                    noBadge
                    sources={sources}
                    tooltipPlacement="bottom"
                />
            </Box>
        </Box>
    );
};

const SSOCoverageInfo = ({
    hideSSOCoverage,
    tenantApp
}: {
    hideSSOCoverage: boolean;
    tenantApp: TenantApp;
}) => {
    const { t } = useTranslation();
    const ssoSupport = SSOUtils.tenantAppGetSSOSupport(tenantApp);

    return (
        !hideSSOCoverage &&
        ssoSupport.some(
            (support) => support.globalSupport || support.tenantUse
        ) && (
            <Box
                data-testid="appHeaderSSOCoverageInfo"
                display="flex"
                flexDirection="column"
            >
                <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
                >
                    {t('Common.ssoCoverage')}
                </Typography>
                <Box mt="4px">
                    <SSOSupportIcons
                        enableTooltip
                        justify="flex-start"
                        ssoSupport={ssoSupport}
                        tenantApp={tenantApp}
                        tooltipPlacement="bottom"
                    />
                </Box>
            </Box>
        )
    );
};

const NameInfo = ({
    editing,
    name,
    noEdit,
    saveName,
    setEditing
}: {
    editing: boolean;
    name: string | undefined;
    noEdit: boolean;
    saveName: (name: string | undefined) => void;
    setEditing: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
    const { t } = useTranslation();
    const [newName, setNewName] = useState(name);

    if (!editing) {
        return (
            <Box
                data-testid="appHeaderNameInfo"
                display="flex"
                alignItems="center"
                key={name}
                sx={{ fontSize: FontSizes[3] }}
            >
                <ApplicationNameLinkCell
                    name={name}
                    nameNoTruncate
                    nameClassname="bold colorSecondary"
                    iconSize="16"
                />
                {!noEdit && (
                    <IconButton
                        data-testid="appHeaderNameInfoEditBtn"
                        data-trackid="appHeader_name_edit"
                        size="small"
                        color="secondary"
                        style={{ marginLeft: '4px' }}
                        onClick={() => setEditing(true)}
                    >
                        <Icon src="Pencil" size={FontSizes[2]} />
                    </IconButton>
                )}
            </Box>
        );
    }

    return (
        <Box display="flex" alignContent="center">
            <TextField
                inputProps={{
                    'data-testid': 'appHeaderNameInfoInput'
                }}
                label={t('Common.app_name')}
                onChange={(e) => setNewName(e.target.value)}
                size="small"
                type="text"
                value={newName}
                variant="outlined"
            />
            <IconButton
                data-testid="appHeaderNameInfoSaveNameBtn"
                data-trackid="appHeader_name_edit_save"
                size="small"
                onClick={() => saveName(newName)}
                sx={{ color: 'icon.success' }}
            >
                <Icon src="CheckCircle" size={FontSizes[4]} />
            </IconButton>
            <IconButton
                data-testid="appHeaderNameInfoCancelSaveNameBtn"
                data-trackid="appHeader_name_edit_cancel"
                size="small"
                onClick={() => {
                    setEditing(false);
                    setNewName(name);
                }}
                sx={{ color: 'icon.danger' }}
            >
                <Icon src="CloseCircle" size={FontSizes[4]} />
            </IconButton>
        </Box>
    );
};

/**
 * Header component for displaying application information
 * @param {Object} props - Component props
 * @param {boolean} [props.hideSSOCoverage] - Whether to hide SSO coverage info
 * @param {boolean} [props.modalView] - Whether component is shown in a modal
 * @param {boolean} [props.noEdit] - Disable app name editing
 * @param {Function} [props.onClose] - Modal close handler
 * @param {Function} [props.setRerender] - Rerender table
 * @param {boolean} [props.showAppCategory] - Whether to show app category
 * @param {boolean} [props.showAppDescription] - Whether to show app description
 */
export const AppHeader = ({
    hideSSOCoverage,
    modalView, // Whether it is shown within a modal and we need the x close button
    noEdit, // Should not be able to edit the app name
    onClose, // What happens when one closes the modal
    setRerender,
    showAppCategory,
    showAppDescription
}: {
    hideSSOCoverage?: boolean;
    modalView?: boolean;
    noEdit?: boolean;
    onClose?: () => void;
    setRerender?: React.Dispatch<React.SetStateAction<boolean>>;
    showAppCategory?: boolean;
    showAppDescription?: boolean;
}) => {
    const { t } = useTranslation();
    const { clientId } = useParams();
    const [editing, setEditing] = useState(false);
    const [classifyAppOpen, setClassifyAppOpen] = useState(false);

    const {
        loading,
        refetchTenantApps,
        tenantApp: singleTenantApp
    } = useTenantAppContext();

    const { putTenantApplicationLink } = usePutTenantApplicationLink();

    const { defaultCategoryId } = useGetDefaultCategoryId();

    const application = singleTenantApp?.application;

    const lifecycleStage: LifecycleStatus | undefined = singleTenantApp?.status;
    const managedVal: ManagementState | undefined =
        singleTenantApp?.managementStage;

    const name = singleTenantApp?.name;

    const sources = singleTenantApp?.users
        ? Helpers.getConsolidatesSourcesFromUsers(singleTenantApp?.users)
        : null;

    const workatoConnection = singleTenantApp?.workatoConnection;
    const workatoProvider = application?.workatoProvider;

    const saveName = async (newName: string | undefined) => {
        const trimmedName = name?.trim();
        const trimmedNewName = newName?.trim();

        if (trimmedName === trimmedNewName) {
            setEditing(false);
            return;
        }
        try {
            await putTenantApplicationLink({
                id: singleTenantApp?.id,
                params: { name: trimmedNewName, platformTenantId: clientId }
            });
            Notify.s(t('Application.saveName.success'));
            refetchTenantApps();
        } catch (e) {
            const err = t('Application.saveName.fail');
            debug.e(err, e);
            Notify.e(err);
        }
        setEditing(false);
    };

    return (
        <Box
            display="flex"
            flexDirection="column"
            data-testid="appHeader"
            data-trackid="sharedComponentsHeadersAppHeader"
            sx={{
                color: 'text.primary',
                fontSize: FontSizes[1],
                textTransform: 'none'
            }}
        >
            {loading && <CircularProgress />}
            {/* Application doesn't exist */}
            {!loading && !application && (
                <>
                    <Typography variant="h4">
                        {t('Common.noAppFound.title')}
                    </Typography>
                    <Divider
                        sx={{
                            borderColor: 'border.primary',
                            marginBottom: '16px',
                            marginTop: '16px'
                        }}
                    />
                    <Typography variant="body2">
                        {t('Common.noAppFound.description')}
                    </Typography>
                </>
            )}
            {/* Application exists */}
            {!loading && !!application && (
                <Application data={application}>
                    <Flex
                        sx={{
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}
                    >
                        <Flex sx={{ alignItems: 'center' }}>
                            <NameInfo
                                editing={editing}
                                name={name}
                                noEdit={noEdit || !singleTenantApp.classified}
                                saveName={saveName}
                                setEditing={setEditing}
                            />
                            {!singleTenantApp.classified && (
                                <Box>
                                    <Button
                                        data-trackid="appHeader_classify"
                                        size="compact"
                                        onClick={() => {
                                            setClassifyAppOpen(true);
                                        }}
                                        sx={{ marginLeft: '16px' }}
                                    >
                                        {t('Common.classify')}
                                    </Button>
                                </Box>
                            )}
                        </Flex>
                        {modalView && (
                            <IconButton
                                data-testid="appHeaderCloseBtn"
                                data-trackid="appHeader_name_edit_close"
                                aria-label="close"
                                sx={{ color: 'icon.primary' }}
                                onClick={() => {
                                    if (onClose) onClose();
                                }}
                            >
                                <Icon src="Close" size="16px" />
                            </IconButton>
                        )}
                        {classifyAppOpen && (
                            <BulkClassifyModal
                                applications={[singleTenantApp]}
                                closeModal={() => {
                                    setClassifyAppOpen(false);
                                }}
                                defaultCategoryId={defaultCategoryId}
                                onSave={(saved: boolean | object) => {
                                    setClassifyAppOpen(false);
                                    if (saved && setRerender) {
                                        setRerender(true);
                                    }
                                }}
                                open={classifyAppOpen}
                            />
                        )}
                    </Flex>
                    <Divider
                        sx={{
                            borderColor: 'border.primary',
                            marginBottom: '16px',
                            marginTop: '16px'
                        }}
                    />
                    <Box
                        display="flex"
                        alignItems="baseline"
                        sx={{ flexWrap: 'wrap', gap: '32px' }}
                    >
                        {application && (
                            <SSOCoverageInfo
                                hideSSOCoverage={!!hideSSOCoverage}
                                tenantApp={singleTenantApp}
                            />
                        )}
                        {!Helpers.isEmpty(workatoConnection) && (
                            <SaasOpsInfo
                                workatoConnection={workatoConnection!}
                                workatoProvider={workatoProvider!}
                                name={name!}
                                refetchTenantApps={refetchTenantApps}
                            />
                        )}
                        <SourceInfo sources={sources} />
                        {lifecycleStage && (
                            <LifecycleInfo lifecycleStage={lifecycleStage} />
                        )}
                        <ManagedInfo managedVal={managedVal} />
                        {showAppCategory && <AppCategoryInfo />}
                        {application?.publisherName && (
                            <PublisherInfo
                                publisher={application?.publisherName}
                            />
                        )}
                        {showAppDescription && <AppDescriptionInfo />}
                    </Box>
                </Application>
            )}
        </Box>
    );
};
